import { useState, useEffect, useCallback, useRef } from 'react';
import useSWR from 'swr';
import shopifyHelpers from 'frontend/js/shopify';

interface ObviyoState {
  isReady: boolean;
  isPolling: boolean;
  error: Error | null;
  listeners: Set<(state: { isReady: boolean; error: Error | null }) => void>;
}

interface ObviyoRecommendationOptions {
  includeProductInfo?: boolean;
  numResults?: number;
}

// Global state to track Obviyo availability
let globalObviyoState: ObviyoState = {
  isReady: false,
  isPolling: false,
  error: null,
  listeners: new Set()
};

const checkObviyoGlobally = () => {
  if ((window as any).obviyo && (window as any).obviyo.api && (window as any).obviyo.api.$fetchObviyoRecommendation) {
    globalObviyoState.isReady = true;
    globalObviyoState.isPolling = false;

    // Notify all listeners
    globalObviyoState.listeners.forEach((callback) =>
      callback({
        isReady: true,
        error: null
      })
    );

    return true;
  }
  return false;
};

const startGlobalPolling = () => {
  if (globalObviyoState.isPolling || globalObviyoState.isReady) {
    return;
  }

  globalObviyoState.isPolling = true;
  const startTime = Date.now();
  const timeout = 5000; // 5 seconds limit

  const pollInterval = setInterval(() => {
    if (checkObviyoGlobally()) {
      clearInterval(pollInterval);
      return;
    }

    // Stop polling after 5 seconds
    if (Date.now() - startTime > timeout) {
      clearInterval(pollInterval);
      globalObviyoState.isPolling = false;
      globalObviyoState.error = new Error('Obviyo API failed to load within 5 seconds');

      // Notify all listeners of the error
      globalObviyoState.listeners.forEach((callback) =>
        callback({
          isReady: false,
          error: globalObviyoState.error
        })
      );
    }
  }, 100);
};

// SWR fetcher for Obviyo recommendations
const obviyoFetcher = async ([externalId, options]: [string, ObviyoRecommendationOptions]) => {
  if (!globalObviyoState.isReady) {
    throw new Error('Obviyo API is not ready yet');
  }

  if (!externalId) {
    throw new Error('External ID is required');
  }

  const result = await (window as any).obviyo.api.$fetchObviyoRecommendation(externalId, options);
  const products = await shopifyHelpers.getShopifyProductList(result.items);
  return products;
};

const useObviyoRecommendations = (externalId?: string, options: ObviyoRecommendationOptions = {}) => {
  const [isReady, setIsReady] = useState(globalObviyoState.isReady);
  const [obviyoError, setObviyoError] = useState(globalObviyoState.error);
  const listenerRef = useRef(null);

  // Use SWR for data fetching with caching
  const { data: recommendations, error, isLoading, mutate } = useSWR(
    isReady && externalId ? [externalId, options] : null,
    obviyoFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 5 * 60 * 1000, // 5 minutes
    }
  );

  useEffect(() => {
    // Create a listener function for this hook instance
    const listener = ({ isReady: ready, error: err }) => {
      setIsReady(ready);
      setObviyoError(err);
    };

    listenerRef.current = listener;
    globalObviyoState.listeners.add(listener);

    // Check immediately if already ready
    if (globalObviyoState.isReady) {
      setIsReady(true);
      setObviyoError(null);
    } else if (globalObviyoState.error) {
      setObviyoError(globalObviyoState.error);
    } else {
      // Start global polling if not already started
      if (checkObviyoGlobally()) {
        setIsReady(true);
      } else {
        startGlobalPolling();
      }
    }

    // Cleanup listener on unmount
    return () => {
      if (listenerRef.current) {
        globalObviyoState.listeners.delete(listenerRef.current);
      }
    };
  }, []);

  // Legacy fetchRecommendations function for compatibility
  const fetchRecommendations = useCallback(
    async (id: string, opts: ObviyoRecommendationOptions = {}) => {
      if (!isReady) {
        throw new Error('Obviyo API is not ready yet');
      }
      const products = await obviyoFetcher([id, opts]);
      // Trigger SWR revalidation
      mutate(products, false);
      return products;
    },
    [isReady, mutate]
  );

  return {
    isReady,
    loading: isLoading,
    error: obviyoError || error,
    recommendations,
    fetchRecommendations,
    clearRecommendations: () => mutate(undefined, false)
  };
};

export default useObviyoRecommendations;
